<template>
  <section v-show="active" class="app-modal__section">
    <div class="app-modal__section-head customer-general__head">
      <div>
        <h4>{{ text.modal.form.general.title }}</h4>
        <p>{{ text.modal.form.general.subtitle }}</p>
      </div>

      <div
        v-if="canShowExistingLogo"
        class="customer-general__logo-card-wrap"
      >
        <AppLogoBadge
          :src="existingLogoUrl"
          :alt="text.modal.form.general.fields.logoPreviewAlt"
          variant="modal"
          :show-remove="true"
          :remove-aria-label="text.modal.form.general.actions.removeLogo"
          :remove-title="text.modal.form.general.actions.removeLogo"
          @remove="$emit('logo-remove')"
        />
      </div>
    </div>

    <div class="app-modal__grid two-columns">
      <label class="app-modal-field">
        <span>{{ text.modal.form.general.fields.name }}</span>
        <input
          v-model="form.name"
          type="text"
          class="form-control"
          :placeholder="text.modal.form.general.placeholders.name"
          @blur="v$.form.name.$touch()"
          :class="{ 'is-invalid': v$.form.name.$error }"
        />
        <div class="invalid-feedback d-block" v-if="v$.form.name.$error">
          <span v-if="v$.form.name.$errors.length">
            {{ v$.form.name.$errors[0].$message }}
          </span>
        </div>
      </label>

      <div class="app-modal-field">
        <span>{{ text.modal.form.general.fields.admissionDate }}</span>
        <VueDatePicker
          v-model="form.admissionDate"
          class="app-modal-datepicker"
          menu-class-name="app-modal-datepicker-menu"
          :placeholder="text.modal.form.general.placeholders.admissionDate"
          :enable-time-picker="false"
          auto-apply
          locale="es-MX"
          format="dd/MM/yyyy"
          hide-input-icon
          :show-now-button="true"
          :now-button-label="text.modal.form.general.date.nowLabel"
        />
      </div>

      <label class="app-modal-field full-width">
        <span>{{ text.modal.form.general.fields.logo }}</span>

        <div v-if="logoPreviewUrl" class="customer-general__logo-preview-wrap">
          <AppImageLoader
            :src="logoPreviewUrl"
            :alt="text.modal.form.general.fields.logoPreviewAlt"
            fit="contain"
            class="customer-general__logo-preview"
          />
          <button
            type="button"
            class="customer-general__logo-preview-trash"
            :aria-label="text.modal.form.general.actions.removeLogo"
            :title="text.modal.form.general.actions.removeLogo"
            @click="$emit('logo-remove')"
          >
            <i class="fa fa-trash" aria-hidden="true"></i>
          </button>
        </div>

        <div class="customer-general__file-upload">
          <input
            ref="logoInput"
            type="file"
            class="customer-general__file-input"
            accept="image/*"
            @change="onLogoInputChange"
          />
          <button
            type="button"
            class="app-header-btn app-header-btn--secondary customer-general__file-trigger"
            @click="openLogoPicker"
          >
            {{ text.modal.form.general.actions.selectLogo }}
          </button>
          <span class="customer-general__file-name">
            {{ logoFileName || text.modal.form.general.placeholders.logoEmpty }}
          </span>
          <button
            v-if="logoPreviewUrl"
            type="button"
            class="customer-general__file-clear"
            :aria-label="text.modal.form.general.actions.removeLogo"
            :title="text.modal.form.general.actions.removeLogo"
            @click="$emit('logo-remove')"
          >
            <i class="fa fa-trash" aria-hidden="true"></i>
          </button>
        </div>
      </label>

      <label class="app-modal-field">
        <span>{{ text.modal.form.general.fields.website }}</span>
        <input v-model="form.website" type="text" class="form-control" :placeholder="text.modal.form.general.placeholders.website" />
      </label>

      <div class="app-modal-field">
        <span>{{ text.modal.form.general.fields.status }}</span>
        <div class="app-modal-checkbox-row">
          <label class="app-modal-toggle">
            <input
              type="checkbox"
              :checked="isStatusActive"
              @change="setStatus('Activo')"
            />
            <span>{{ text.modal.form.general.fields.active }}</span>
          </label>

          <label class="app-modal-toggle">
            <input
              type="checkbox"
              :checked="isStatusInactive"
              @change="setStatus('Inactivo')"
            />
            <span>{{ text.modal.form.general.fields.inactive }}</span>
          </label>
        </div>
      </div>

      <label class="app-modal-field full-width">
        <span>{{ text.modal.form.general.fields.comment }}</span>
        <textarea
          v-model="form.comment"
          class="form-control app-modal-field__textarea"
          rows="6"
          :placeholder="text.modal.form.general.placeholders.comment"
        ></textarea>
      </label>
    </div>
  </section>
</template>

<script>
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useDomainText } from '@/composables/text/useDomainText';
import AppImageLoader from '@/components/common/AppImageLoader.vue';
import AppLogoBadge from '@/components/common/AppLogoBadge.vue';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomerSectionGeneral',
  components: {
    VueDatePicker,
    AppImageLoader,
    AppLogoBadge,
  },
  emits: ['logo-change', 'logo-remove'],
  props: {
    active: { type: Boolean, default: false },
    form: { type: Object, required: true },
    v$: { type: Object, required: true },
    isEditMode: { type: Boolean, default: false },
    existingLogoUrl: { type: String, default: '' },
    logoPreviewUrl: { type: String, default: '' },
    logoMarkedForDelete: { type: Boolean, default: false },
    logoFileName: { type: String, default: '' },
  },
  data() {
    return {
      text,
    };
  },
  computed: {
    isStatusActive() {
      return String(this.form?.status || 'Activo').trim().toLowerCase() === 'activo';
    },
    isStatusInactive() {
      return String(this.form?.status || '').trim().toLowerCase() === 'inactivo';
    },
    canShowExistingLogo() {
      if (!this.isEditMode || this.logoMarkedForDelete) {
        return false;
      }

      return String(this.existingLogoUrl || '').trim() !== '';
    },
  },
  methods: {
    openLogoPicker() {
      const input = this.$refs.logoInput;
      if (input && typeof input.click === 'function') {
        input.click();
      }
    },
    setStatus(statusLabel) {
      this.form.status = statusLabel;
    },
    onLogoInputChange(event) {
      const target = event?.target;
      const file = target?.files && target.files[0] ? target.files[0] : null;
      this.$emit('logo-change', file);
    },
  },
};
</script>

<style scoped>
.customer-general__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.customer-general__logo-preview-trash:hover,
.customer-general__file-clear:hover {
  color: #be123c;
  background: #ffffff;
}

.customer-general__logo-card-wrap {
  display: inline-flex;
}

.customer-general__logo-preview-wrap {
  position: relative;
  margin-top: 12px;
  border: 1px dashed #cfd8e3;
  border-radius: 10px;
  background: #f8fbff;
  padding: 10px;
  min-height: 110px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.customer-general__logo-preview {
  width: 100%;
  height: 200px;
}

.customer-general__logo-preview-trash {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 30px;
  height: 30px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: rgba(248, 250, 252, 0.95);
  color: #e11d48;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.customer-general__file-upload {
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  min-height: 44px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ffffff;
  margin-top: 10px;
}

.customer-general__file-input {
  display: none;
}

.customer-general__file-trigger {
  min-height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
  flex-shrink: 0;
}

.customer-general__file-name {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  font-size: 12px;
  color: #4b5563;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.customer-general__file-clear {
  width: 28px;
  height: 28px;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: rgba(248, 250, 252, 0.95);
  color: #e11d48;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.app-modal-checkbox-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
}

.app-modal-checkbox-row .app-modal-toggle {
  margin: 0;
  min-height: 38px;
}

@media (max-width: 860px) {
  .customer-general__head {
    flex-direction: column;
  }
}
</style>
